import React, { useEffect, useState, useMemo } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { Link, useSearchParams } from 'react-router-dom';
import { isUndefined } from 'lodash';
import pluralize from 'pluralize';
import { InsuranceClaimList } from '@models/InsuranceClaim';
import { tInsuranceTypeEnum } from '@models/InsuranceType';
import { Button, Card } from 'react-bootstrap';
import DataTable, { TableColumn } from 'react-data-table-component';
import { selectIsInsurer } from '@components/Auth';
import { ClaimBadge } from '@components/Base';
import { formatDateTime } from '@utils/datetime';
import { KAMINARI_DEFAULT_PER_PAGE } from '@config';

import { routeUserDashboardInsuranceClaimDetailsPage } from '../route';
import { claimsFetch, ClaimsFetchPayload } from './action';
import { selectInsuranceClaimListPageReducer } from './selector';
import { ClaimControlForm } from './claim-control-form';

const customStyles = {
  headCells: {
    style: {
      fontWeight: 'bold',
    },
  },
};

const ViewDetailsButton = ({ row }: { row: InsuranceClaimList }) => {
  const { t } = useTranslation();

  return (
    <div data-testid="claim-item">
      <Button
        as={Link}
        to={routeUserDashboardInsuranceClaimDetailsPage({
          claimableType: row.claimableType,
          claimableId: row.claimableId,
        })}
        variant="outline-primary"
        size="sm"
      >
        {t('common:viewDetails')}
      </Button>
    </div>
  );
};

const useColumns = () => {
  const { t } = useTranslation();
  const isInsurer = useSelector(selectIsInsurer);

  return useMemo(
    () =>
      [
        {
          cell: (row) => <ViewDetailsButton row={row} />,
          button: 'true',
          center: 'true',
          width: '120px',
        },
        {
          name: t('common:billingType'),
          selector: (row) => row.billingTypeCode,
          width: '120px',
        },
        {
          name: t('common:policyNumber'),
          selector: (row) => row.policyNumber,
          width: '180px',
        },
        {
          name: t('common:claimNumber'),
          selector: (row) => row.claimNumber,
          width: '180px',
        },
        {
          name: pluralize.singular(t('common:insuranceProducts')),
          selector: (row) => row.insuranceType,
          format: (row) => tInsuranceTypeEnum(row.insuranceType),
          width: '220px',
        },
        {
          name: t('insuranceClaimDetails.registerDate'),
          selector: (row) => row.createdAt,
          format: (row) => formatDateTime(row.createdAt),
          width: '200px',
        },
        {
          name: t('common:status'),
          cell: (row) => <ClaimBadge status={row.status} />,
        },
        {
          name: t('common:memberName'),
          cell: ({ insurancePolicyUser }) => insurancePolicyUser?.name,
          width: '120px',
        },
        {
          name: t('common:creator'),
          cell: ({ creator }) => creator?.name,
          width: '120px',
        },
      ] as TableColumn<ClaimList>[],
    [isInsurer],
  );
};

const InsuranceClaimListPage = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const columns = useColumns();
  const [searchParams, setSearchParams] = useSearchParams();
  const { claims, pagination } = useSelector(selectInsuranceClaimListPageReducer);
  const [isLoading, setIsLoading] = useState(true);

  const handlePageChange = (page: number) => {
    setSearchParams({ ...Object.fromEntries(searchParams), page: page.toString() });
  };

  const handlePerRowsChange = (newPerPage: number, page: number) => {
    setSearchParams({ ...Object.fromEntries(searchParams), page: page.toString(), perPage: newPerPage.toString() });
  };

  useEffect(() => {
    setIsLoading(true);
    const payload: ClaimsFetchPayload = Object.fromEntries(searchParams);
    dispatch(claimsFetch(payload));
  }, [searchParams]);

  useEffect(() => {
    if (isUndefined(claims)) return;
    setIsLoading(false);
  }, [claims]);

  return (
    <section>
      <div className="d-flex justify-content-between mb-4">
        <h5>{t('common:claim')}</h5>
      </div>
      <div className="mb-4">
        <ClaimControlForm />
      </div>
      <Card>
        <Card.Body>
          <DataTable
            columns={columns}
            data={claims ?? []}
            customStyles={customStyles}
            fixedHeader
            pagination
            paginationServer
            progressPending={isLoading}
            paginationPerPage={pagination?.perPage ?? KAMINARI_DEFAULT_PER_PAGE}
            paginationTotalRows={pagination?.total ?? 0}
            onChangeRowsPerPage={handlePerRowsChange}
            onChangePage={handlePageChange}
          />
        </Card.Body>
      </Card>
    </section>
  );
};

export { InsuranceClaimListPage };
